import React, { useMemo, useState, createElement } from 'react';
import { motion } from 'framer-motion';
import { DownloadIcon, ChevronDownIcon, ChevronUpIcon, FilterIcon } from 'lucide-react';
import { useStore } from '../../context/StoreContext';
import { OrderStatus } from '../../context/StoreContext';
const ALL_STATUSES: OrderStatus[] = ['Placed', 'Confirmed', 'Shipped', 'Delivered', 'Cancelled'];
const statusColors: Record<OrderStatus, string> = {
  Placed: 'bg-blue-100 text-blue-700',
  Confirmed: 'bg-indigo-100 text-indigo-700',
  Shipped: 'bg-orange-100 text-orange-700',
  Delivered: 'bg-green-100 text-green-700',
  Cancelled: 'bg-red-100 text-red-700'
};
export function AdminOrders() {
  const {
    orders,
    updateOrderStatus
  } = useStore();
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'All'>('All');
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const filtered = useMemo(() => {
    if (statusFilter === 'All') return orders;
    return orders.filter(o => o.status === statusFilter);
  }, [orders, statusFilter]);
  const exportCSV = () => {
    const headers = ['Order ID', 'Customer', 'Email', 'Phone', 'Date', 'Items', 'Total', 'Status', 'Address'];
    const rows = orders.map(o => [o.id, o.address.name, '', o.address.phone, new Date(o.date).toLocaleDateString('en-IN'), o.items.map(i => `${i.product.name}(${i.size}x${i.quantity})`).join('; '), o.totalAmount, o.status, `${o.address.address}, ${o.address.city}, ${o.address.state} - ${o.address.pincode}`]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `skybrand_orders_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const getNextStatus = (current: OrderStatus): OrderStatus | null => {
    const flow: OrderStatus[] = ['Placed', 'Confirmed', 'Shipped', 'Delivered'];
    const idx = flow.indexOf(current);
    if (idx >= 0 && idx < flow.length - 1) return flow[idx + 1];
    return null;
  };
  return <div className="space-y-6" data-id="element-513">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-514">
        <div className="flex items-center gap-3" data-id="element-515">
          <FilterIcon className="w-4 h-4 text-slate-400" data-id="element-516" />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as OrderStatus | 'All')} className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" data-id="element-517">
            <option value="All" data-id="element-518">All Orders ({orders.length})</option>
            {ALL_STATUSES.map(s => <option key={s} value={s} data-id="element-519">
                {s} ({orders.filter(o => o.status === s).length})
              </option>)}
          </select>
        </div>
        <button onClick={exportCSV} className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors" data-id="element-520">
          <DownloadIcon className="w-4 h-4" data-id="element-521" /> Export CSV
        </button>
      </div>

      {/* Orders */}
      <div className="space-y-3" data-id="element-522">
        {filtered.map((order, index) => {
        const isExpanded = expandedOrder === order.id;
        const nextStatus = getNextStatus(order.status);
        return <motion.div key={order.id} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.03
        }} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-523">
              <button onClick={() => setExpandedOrder(isExpanded ? null : order.id)} className="w-full px-4 md:px-6 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors" data-id="element-524">
                <div className="flex items-center gap-4 flex-1 min-w-0" data-id="element-525">
                  <div className="min-w-0" data-id="element-526">
                    <p className="text-sm font-mono text-slate-700 truncate" data-id="element-527">
                      {order.id}
                    </p>
                    <p className="text-xs text-slate-400" data-id="element-528">
                      {new Date(order.date).toLocaleDateString('en-IN')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-id="element-529">
                  <span className="text-sm font-medium text-slate-900 hidden sm:block" data-id="element-530">
                    ₹{order.totalAmount}
                  </span>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusColors[order.status]}`} data-id="element-531">
                    {order.status}
                  </span>
                  {isExpanded ? <ChevronUpIcon className="w-4 h-4 text-slate-400" data-id="element-532" /> : <ChevronDownIcon className="w-4 h-4 text-slate-400" data-id="element-533" />}
                </div>
              </button>

              {isExpanded && <div className="px-4 md:px-6 pb-4 border-t border-slate-100" data-id="element-534">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4" data-id="element-535">
                    {/* Items */}
                    <div data-id="element-536">
                      <h4 className="text-sm font-semibold text-slate-900 mb-3" data-id="element-537">
                        Items
                      </h4>
                      <div className="space-y-2" data-id="element-538">
                        {order.items.map(item => <div key={`${item.product.id}-${item.size}`} className="flex items-center gap-3" data-id="element-539">
                            <img src={item.product.images[0]} alt={item.product.name} className="w-10 h-12 object-cover rounded bg-slate-100" data-id="element-540" />
                            <div className="flex-1 min-w-0" data-id="element-541">
                              <p className="text-sm text-slate-700 truncate" data-id="element-542">
                                {item.product.name}
                              </p>
                              <p className="text-xs text-slate-400" data-id="element-543">
                                Size: {item.size} × {item.quantity}
                              </p>
                            </div>
                            <span className="text-sm font-medium" data-id="element-544">
                              ₹{item.product.sellingPrice * item.quantity}
                            </span>
                          </div>)}
                      </div>
                    </div>

                    {/* Address & Actions */}
                    <div data-id="element-545">
                      <h4 className="text-sm font-semibold text-slate-900 mb-3" data-id="element-546">
                        Delivery Address
                      </h4>
                      <div className="text-sm text-slate-600 space-y-1 mb-4" data-id="element-547">
                        <p className="font-medium text-slate-900" data-id="element-548">
                          {order.address.name}
                        </p>
                        <p data-id="element-549">{order.address.address}</p>
                        <p data-id="element-550">
                          {order.address.city}, {order.address.state} -{' '}
                          {order.address.pincode}
                        </p>
                        <p data-id="element-551">Phone: {order.address.phone}</p>
                      </div>

                      <div className="flex items-center gap-2 mb-3" data-id="element-552">
                        <span className="text-sm font-medium text-slate-700" data-id="element-553">
                          Total:
                        </span>
                        <span className="text-lg font-bold text-slate-900" data-id="element-554">
                          ₹{order.totalAmount}
                        </span>
                        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded" data-id="element-555">
                          COD
                        </span>
                      </div>

                      {/* Status Update */}
                      {order.status !== 'Cancelled' && order.status !== 'Delivered' && <div className="flex flex-col gap-2 pt-3 border-t border-slate-100" data-id="element-556">
                            <label className="text-xs font-medium text-slate-500" data-id="element-557">
                              Update Status
                            </label>
                            <div className="flex gap-2" data-id="element-558">
                              <select defaultValue={order.status} onChange={e => updateOrderStatus(order.id, e.target.value as OrderStatus)} className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" data-id="element-559">
                                {ALL_STATUSES.filter(s => s !== 'Cancelled').map(s => <option key={s} value={s} data-id="element-560">
                                    {s}
                                  </option>)}
                              </select>
                            </div>
                            <div className="flex gap-2" data-id="element-561">
                              <input type="date" onChange={e => {
                      if (e.target.value) {
                        updateOrderStatus(order.id, order.status, e.target.value);
                      }
                    }} className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" placeholder="Set delivery date" data-id="element-562" />
                            </div>
                            {nextStatus && <button onClick={() => updateOrderStatus(order.id, nextStatus)} className="px-4 py-2 bg-sky-600 text-white text-sm font-medium rounded-lg hover:bg-sky-700 transition-colors" data-id="element-563">
                                Mark as {nextStatus}
                              </button>}
                          </div>}
                    </div>
                  </div>
                </div>}
            </motion.div>;
      })}

        {filtered.length === 0 && <div className="bg-white rounded-xl border border-slate-200 py-12 text-center text-slate-400" data-id="element-564">
            No orders found
          </div>}
      </div>
    </div>;
}